import React from "react";
import styled from "@emotion/styled/macro";
import { Checkbox, Typography } from "@sunrun/experience-ui-components";

type ResponseListOption = {
  label: string;
  value: string;
};

type CheckboxGroupProps = {
  options: ResponseListOption[];
  values: string[];
  onChange: (values: string[]) => void;
  layout: "column" | "row";
  name: string;
};

const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options,
  values,
  onChange,
  layout,
  name,
}) => {
  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    if (checked) {
      onChange([...values, optionValue]);
    } else {
      onChange(values.filter((v) => v !== optionValue));
    }
  };

  const hasMoreThanThree = options.length > 3;

  return (
    <CheckboxGroupContainer layout={layout} hasMoreThanThree={hasMoreThanThree}>
      {options.map((option) => (
        <CheckboxOption key={option.value}>
          <Checkbox
            id={`${name}-${option.value}`}
            name={name}
            checked={values.includes(option.value)}
            onChange={(e) =>
              handleCheckboxChange(option.value, e.target.checked)
            }
            data-testid={`checkbox-${name}-${option.value}`}
          />
          <CheckboxLabel htmlFor={`${name}-${option.value}`}>
            <Typography variant="span">{option.label}</Typography>
          </CheckboxLabel>
        </CheckboxOption>
      ))}
    </CheckboxGroupContainer>
  );
};

const CheckboxGroupContainer = styled.div<{
  layout: "column" | "row";
  hasMoreThanThree: boolean;
}>`
  display: flex;
  flex-direction: ${({ layout }) => (layout === "column" ? "column" : "row")};
  gap: 16px;
  flex-wrap: ${({ layout }) => (layout === "row" ? "wrap" : "nowrap")};

  ${({ hasMoreThanThree }) =>
    hasMoreThanThree &&
    `
    @media (max-width: 768px) {
      & > div {
        flex: 0 0 calc(50% - 8px);
      }
    }
  `}
`;

const CheckboxOption = styled.div`
  display: flex;
  align-items: center;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  display: flex;
  align-items: center;
  user-select: none;
`;

export { CheckboxGroup };
export type { CheckboxGroupProps };
